import { createTheme, PaletteMode, Theme } from '@mui/material';
import { CSSProperties } from 'react';

export interface ICallScribeSpeakerTone {
  ground: string;
  groundBorder: string;
  paper: string;
  paperBorder: string;
  stageSolid: string;
  stageText: string;
  paperSolid: string;
}

export interface ICallScribePalette {
  ground: string;
  groundEdge: string;
  glass: string;
  glassStrong: string;
  glassBorder: string;
  glassFallback: string;
  onGround: string;
  onGroundMuted: string;
  onGroundFaint: string;
  accent: string;
  speakerTech: string;
  speakerTechBorder: string;
  speakerCustomer: string;
  speakerCustomerBorder: string;
  speaker1: ICallScribeSpeakerTone;
  speaker2: ICallScribeSpeakerTone;
  speaker3: ICallScribeSpeakerTone;
  speaker4: ICallScribeSpeakerTone;
  speaker5: ICallScribeSpeakerTone;
  speaker6: ICallScribeSpeakerTone;
  ringCustomer: string;
  live: string;
  end: string;
  warn: string;
  info: string;
  neutral: string;
  bg: string;
  surface: string;
  surface2: string;
  surface3: string;
  line: string;
  lineStrong: string;
  fieldLine: string;
  controlLine: string;
  text: string;
  text2: string;
  text3: string;
  brand: string;
  brandInk: string;
  call: string;
  callInk: string;
  callSoft: string;
  callText: string;
  endInk: string;
  pos: string;
  posBg: string;
  neg: string;
  negBg: string;
  neu: string;
  neuBg: string;
  warnBg: string;
  infoBg: string;
  focus: string;
  scrim: string;
  shadowFloat: string;
  stageCall: string;
  stageCallInk: string;
  stageEnd: string;
  stageEndInk: string;
  stageEndText: string;
  stageWarn: string;
  stageWarnInk: string;
  stageInfo: string;
  stageFocus: string;
}

const speakerTone = (
  rgb: string,
  stageSolid: string,
  stageText: string,
  paperSolid: string,
): ICallScribeSpeakerTone => ({
  ground: `rgba(${rgb}, 0.28)`,
  groundBorder: `rgba(${rgb}, 0.64)`,
  paper: `rgba(${rgb}, 0.16)`,
  paperBorder: `rgba(${rgb}, 0.55)`,
  stageSolid,
  stageText,
  paperSolid,
});

const getCallScribePalette = (mode: PaletteMode): ICallScribePalette => {
  const dark = mode === 'dark';
  return {
    ground: dark ? '#070D1F' : '#0B1736',
    groundEdge: dark ? '#070D1F' : '#0B1736',
    glass: 'rgba(255, 255, 255, 0.06)',
    glassStrong: 'rgba(255, 255, 255, 0.10)',
    glassBorder: 'rgba(255, 255, 255, 0.14)',
    glassFallback: dark ? '#161C2D' : '#1A2542',
    onGround: '#F3F6FC',
    onGroundMuted: '#B7C1DA',
    onGroundFaint: '#8E9AB8',
    accent: dark ? '#F0BD3A' : '#B7791F',
    speakerTech: 'rgba(240, 189, 58, 0.24)',
    speakerTechBorder: 'rgba(240, 189, 58, 0.58)',
    speakerCustomer: 'rgba(255, 255, 255, 0.12)',
    speakerCustomerBorder: 'rgba(255, 255, 255, 0.22)',
    speaker1: speakerTone('240, 189, 58', '#F0BD3A', '#F0BD3A', dark ? '#F0BD3A' : '#9A6B00'),
    speaker2: speakerTone('45, 182, 163', '#2DB6A3', '#2DB6A3', dark ? '#2DB6A3' : '#0E7A6C'),
    speaker3: speakerTone('155, 123, 224', '#9B7BE0', '#B79CF0', dark ? '#B79CF0' : '#6B49C0'),
    speaker4: speakerTone('224, 108, 139', '#E06C8B', '#F08AA6', dark ? '#F08AA6' : '#B0385A'),
    speaker5: speakerTone('79, 163, 224', '#4FA3E0', '#6FB6EE', dark ? '#6FB6EE' : '#1F63B0'),
    speaker6: speakerTone('141, 190, 63', '#8DBE3F', '#A8D45B', dark ? '#A8D45B' : '#4F7A12'),
    ringCustomer: 'rgba(255, 255, 255, 0.85)',
    live: dark ? '#4CC38A' : '#1E7B45',
    end: dark ? '#F0685A' : '#C73A2B',
    warn: dark ? '#F5C46B' : '#8A5300',
    info: dark ? '#9DBCFF' : '#2B54B8',
    neutral: dark ? '#8793B0' : '#5B6781',
    bg: dark ? '#0A1124' : '#EDF0F5',
    surface: dark ? '#101A33' : '#FFFFFF',
    surface2: dark ? '#15213F' : '#F5F7FA',
    surface3: dark ? '#1B2A4D' : '#E9EDF4',
    line: dark ? '#243459' : '#DDE3EC',
    lineStrong: dark ? '#34467A' : '#C3CCDA',
    fieldLine: dark ? '#5F719F' : '#7A88A3',
    controlLine: dark ? '#6A7CA9' : '#6E7C98',
    text: dark ? '#EEF2FA' : '#0B1736',
    text2: dark ? '#AAB5CE' : '#46526B',
    text3: dark ? '#8793B0' : '#5B6781',
    brand: dark ? '#6F93FF' : '#1B3670',
    brandInk: dark ? '#06122E' : '#FFFFFF',
    call: dark ? '#4CC38A' : '#1E7B45',
    callInk: dark ? '#06210F' : '#FFFFFF',
    callSoft: dark ? '#12301F' : '#E3F4EA',
    callText: dark ? '#6FDCA2' : '#16703D',
    endInk: dark ? '#2B0904' : '#FFFFFF',
    pos: dark ? '#6FDCA2' : '#16703D',
    posBg: dark ? '#12301F' : '#E3F4EA',
    neg: dark ? '#FF9A8E' : '#B3261E',
    negBg: dark ? '#3A1713' : '#FBE7E4',
    neu: dark ? '#AAB5CE' : '#46526B',
    neuBg: dark ? '#1B2A4D' : '#E8ECF3',
    warnBg: dark ? '#3A2A0B' : '#FBF0DC',
    infoBg: dark ? '#15264F' : '#E4ECFC',
    focus: dark ? '#8FB2FF' : '#2B54B8',
    scrim: dark ? 'rgba(3, 7, 18, 0.6)' : 'rgba(11, 23, 54, 0.45)',
    shadowFloat: dark
      ? '0 0 0 1px rgba(255,255,255,0.04), 0 12px 32px rgba(0,0,0,0.55)'
      : '0 1px 2px rgba(11,23,54,0.06), 0 10px 28px rgba(11,23,54,0.14)',
    stageCall: '#4CC38A',
    stageCallInk: '#06210F',
    stageEnd: '#F0685A',
    stageEndInk: '#2B0904',
    stageEndText: '#FF9A8E',
    stageWarn: '#F5C46B',
    stageWarnInk: '#2B1D00',
    stageInfo: '#9DBCFF',
    stageFocus: '#9DBCFF',
  };
};

export interface IPodStatusTone {
  bg: string;
  fg: string;
}

export interface IPodPalette {
  status: {
    success: IPodStatusTone;
    warning: IPodStatusTone;
    error: IPodStatusTone;
    danger: IPodStatusTone;
    neutral: IPodStatusTone;
  };
  accent: string;
  focus: string;
  textMuted: string;
  hover: string;
  strip: { bg: string; line: string };
}

export interface IPodScale {
  radius: { control: number; card: number };
  space: { xs: number; sm: number; md: number; lg: number };
  font: { sm: string; md: string; lg: string };
}

const getPodPalette = (mode: PaletteMode): IPodPalette => {
  const dark = mode === 'dark';
  return {
    status: {
      success: dark ? { bg: '#1E3B2A', fg: '#9FE0B5' } : { bg: '#E6F4EA', fg: '#1E6B3A' },
      warning: dark ? { bg: '#3D2E10', fg: '#F5C46B' } : { bg: '#FFF4E0', fg: '#8A5300' },
      error: dark ? { bg: '#45201E', fg: '#FFB4AB' } : { bg: '#FDECEC', fg: '#B3261E' },
      danger: { bg: '#B3261E', fg: '#FDECEC' },
      neutral: dark ? { bg: '#3A3F4A', fg: '#E1E5EC' } : { bg: '#EEF1F5', fg: '#46526B' },
    },
    accent: dark ? '#DCE7FF' : '#1B3670',
    focus: dark ? '#9DBCFF' : '#1B3670',
    textMuted: dark ? '#E8EBF0' : '#46526B',
    hover: dark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(27, 54, 112, 0.06)',
    strip: dark
      ? { bg: '#1E2127', line: 'rgba(255, 255, 255, 0.2)' }
      : { bg: '#1B3670', line: 'transparent' },
  };
};

export const podScale: IPodScale = {
  radius: { control: 4, card: 8 },
  space: { xs: 4, sm: 8, md: 12, lg: 16 },
  font: { sm: '12px', md: '13px', lg: '14px' },
};

declare module '@mui/material/styles' {
  interface PaletteOptions {
    callScribe?: ICallScribePalette;
    pod?: IPodPalette;
  }

  interface Theme {
    podScale: IPodScale;
    palette: {
      callScribe: ICallScribePalette;
      pod: IPodPalette;
      primary: {
        main: string;
      };
      secondary: {
        main: string;
      };
      warning: {
        main: string;
        light: string;
        dark: string;
      };
      info: {
        main: string;
        light: string;
        dark: string;
      };
      success: {
        main: string;
        light: string;
        dark: string;
      };
      error: {
        main: string;
        light: string;
        dark: string;
      };
      text: {
        primary: string;
        secondary: string;
        disabled: string;
        // Extension-only (MSPPROC-17664): a WCAG-AA-safe stand-in for
        // primary.main wherever the accent color is used as literal text
        // rather than an icon/border/button fill - primary.main itself is
        // deliberately the SAME literal value across light/dark (mobile-app
        // parity, see extension/theme.ts), but that value alone reads at
        // 2.88:1 on a dark-mode card, well under the 4.5:1 text minimum.
        // Optional so the hosted app's getTheme() doesn't need to define it.
        accent?: string;
      };
      background: {
        default: string;
        paper: string;
      };
      divider: string;
      action: {
        hover: string;
        selected: string;
        disabled: string;
        disabledBackground: string;
      };
      surface: {
        page: string;
        card: string;
        cardShadow: string;
        // Extension-only (MSPPROC-17664), all optional so the hosted app's
        // getTheme() below doesn't need to define them.
        section?: string;
        borderCard?: string;
        borderInput?: string;
      };
      topBar: {
        background: string;
        divider: string;
        icon: string;
        hover: string;
        activeBackground: string;
        activeIcon: string;
      };
      // Extension-only (MSPPROC-17664): tinted pill/badge colors, ported
      // from the mobile app's EndUserVerification screen so both surfaces
      // read as the same product. Optional - the hosted app's getTheme()
      // doesn't define these.
      status?: {
        successBg: string;
        successColor: string;
        dangerBg: string;
        dangerColor: string;
        warningBg: string;
        warningColor: string;
        neutralBg: string;
        neutralColor: string;
      };
      textSub?: string;
      mode: PaletteMode;
    };
    typography: {
      fontFamily: string;
      h1: CSSProperties;
      h2: CSSProperties;
      h3: CSSProperties;
      h4: CSSProperties;
      h5: CSSProperties;
      h6: CSSProperties;
      subtitle1: CSSProperties;
      subtitle2: CSSProperties;
      body1: CSSProperties;
      body2: CSSProperties;
      button: CSSProperties;
      caption: CSSProperties;
      overline: CSSProperties;
    };
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
    podScale?: IPodScale;
  }
  interface PaletteOptions {
    surface?: {
      page: string;
      card: string;
      cardShadow: string;
      section?: string;
      borderCard?: string;
      borderInput?: string;
    };
    topBar?: {
      background: string;
      divider: string;
      icon: string;
      hover: string;
      activeBackground: string;
      activeIcon: string;
    };
    status?: {
      successBg: string;
      successColor: string;
      dangerBg: string;
      dangerColor: string;
      warningBg: string;
      warningColor: string;
      neutralBg: string;
      neutralColor: string;
    };
    divider?: string;
    textSub?: string;
  }
  // MUI's own extension point for palette.text - lets createTheme() accept
  // the accent field added to Theme.palette.text above without redeclaring
  // the whole (already non-standard) text shape a second time. See
  // MSPPROC-17664.
  interface TypeText {
    accent?: string;
  }
}

export const getTheme = (mode: PaletteMode): Theme =>
  createTheme({
    palette: {
      primary: {
        main: mode === 'dark' ? '#141414' : '#00142D',
      },
      secondary: {
        main: mode === 'dark' ? '#656565' : '#fff',
      },
      warning: {
        main: '#ed6c02',
      },
      text: {
        primary: mode === 'dark' ? '#fff' : '#000',
        secondary: '#fff',
      },
      background: {
        default: mode === 'dark' ? '#464646' : '#D0D0D0',
      },
      surface: {
        page: mode === 'dark' ? '#656565' : '#D0D0D0',
        card: mode === 'dark' ? 'transparent' : '#fff',
        cardShadow: mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 20, 45, 0.14)',
      },
      topBar: {
        background: mode === 'dark' ? 'transparent' : '#1b3670',
        divider: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'transparent',
        icon: '#fff',
        hover: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.16)',
        activeBackground: mode === 'dark' ? '#141414' : '#fff',
        activeIcon: mode === 'dark' ? '#fff' : '#1b3670',
      },
      callScribe: getCallScribePalette(mode),
      pod: getPodPalette(mode),

      mode,
    },
    podScale,
    typography: {
      fontFamily: "'Nunito', sans-serif",
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#171717' : '#D0D0D0',

            '& .MuiBreadcrumbs-li': {
              cursor: 'pointer',
              color: mode === 'light' ? '#171717' : '#ffffff',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& :checked': {
              color: 'red',
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            color: mode === 'light' ? '#000' : '#fff',
          },
        },
      },
    },
  });
